/**
 * SmartLoader — indicateur de chargement avec timeout + retry.
 *
 * Résout le bug « Chargement… bloqué indéfiniment » : après ``timeoutMs``
 * millisecondes sans résolution, on affiche un message d'erreur clair et
 * un bouton « Réessayer » qui déclenche ``onRetry`` (ou un rechargement de
 * la page si non fourni).
 *
 * Usage :
 *   const [loading, setLoading] = useState(true);
 *   const [error, setError] = useState(null);
 *   ...
 *   {loading && <SmartLoader onRetry={reload} error={error} />}
 */
import React, { useEffect, useState } from "react";
import { Loader2, AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function SmartLoader({
  label = "Chargement en cours…",
  timeoutMs = 12_000,
  onRetry,
  error,
  compact = false,
}) {
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    setTimedOut(false);
    if (error) return; // already in error state
    const t = setTimeout(() => setTimedOut(true), timeoutMs);
    return () => clearTimeout(t);
  }, [timeoutMs, error]);

  const doRetry = () => {
    setTimedOut(false);
    if (onRetry) return onRetry();
    window.location.reload();
  };

  if (error || timedOut) {
    return (
      <div
        data-testid="smart-loader-error"
        className={`flex flex-col items-center justify-center text-center ${compact ? "p-6" : "p-12"} gap-3`}
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-50 text-amber-600">
          <AlertTriangle className="h-6 w-6" />
        </div>
        <div className="max-w-sm">
          <div className="font-medium text-foreground">
            {error ? "Une erreur est survenue" : "Le chargement prend plus de temps que prévu"}
          </div>
          <p className="mt-1.5 text-sm text-muted-foreground">
            {error
              ? (typeof error === "string" ? error : (error.message || "Impossible de récupérer les données."))
              : "Vérifiez votre connexion et réessayez. Si le problème persiste, écrivez-nous à contact@fonbex.fr."}
          </p>
        </div>
        <Button onClick={doRetry} variant="outline" size="sm"
                data-testid="smart-loader-retry">
          <RefreshCw className="mr-2 h-3.5 w-3.5" /> Réessayer
        </Button>
      </div>
    );
  }

  return (
    <div
      data-testid="smart-loader-loading"
      className={`flex flex-col items-center justify-center text-center ${compact ? "p-6" : "p-12"} gap-3`}
    >
      <Loader2 className="h-6 w-6 animate-spin text-accent" />
      <div className="text-sm text-muted-foreground">{label}</div>
    </div>
  );
}
